import React from 'react';
import {
  Card, CircularGaugeChart, ellipsis, FontWeight, Helper, HStack, Icon, ICON_ALERT_CIRCLE_OUTLINE, ICON_INFORMATION_OUTLINE, Message, ModalSize, Portal, Spacing, Stack,
  Text,
  useModal,
} from '@doveit/bricks';
import Markdown from 'react-markdown';
import { ConversionMetric, IssueBelowTarget, WithIssues } from '../../../providers/api/dtos';
import { configByCr, configByCrPerformanceRating } from './constants';
import { template } from '../../../utils/text/text';
import SimpleModal from '../../../components/simple-modal/SimpleModal';
import ManagerConversionRateDetailView from '../manager-conversion-rate-detail-view/ManagerConversionRateDetailView';

export interface ManagerConversionRateSummaryCardProps extends React.AriaAttributes {
  cr: 'cr2' | 'cr3',
  metric: ConversionMetric & WithIssues<IssueBelowTarget>,
}

const ManagerConversionRateSummaryCard: React.FC<ManagerConversionRateSummaryCardProps> = ({
  cr,
  metric,
  ...rest
}) => {
  const agentsBelowTargetModal = useModal();

  const cfgByCr = configByCr[cr];
  const cfgByCrPerformanceRating = metric.status ? configByCrPerformanceRating[metric.status] : configByCrPerformanceRating.NOT_DEFINED;

  const agentsBelowTarget = (metric.issues || [])
    ?.filter(({ type }) => type === 'BELOW_TARGET')
    .map(({ agent, value }) => ({ agent, value }));

  return (
    <>
      <Card {...rest}>
        <Card.Content>
          <Spacing padding={[50, 0]}>
            <HStack
              gap={100}
              alignItems="start"
              justifyContent="space-between"
              wrap="nowrap"
            >
              <HStack
                gap={300}
                alignItems="start"
                wrap="nowrap"
              >
                <div style={{ width: '6.25rem' }}>
                  <Stack
                    gap={150}
                    alignItems="center"
                  >
                    <CircularGaugeChart
                      progress={metric.progress ?? 0}
                      color={cfgByCrPerformanceRating.variant}
                      size="L"
                    />

                    <Text.Caption
                      color={`${cfgByCrPerformanceRating.variant}.default.low`}
                      css={ellipsis()}
                    >
                      {cfgByCr.chartCaption}
                    </Text.Caption>
                  </Stack>
                </div>

                <Spacing padding={[75, 0]}>
                  <Stack gap={150}>
                    {cfgByCrPerformanceRating.title && (
                      <Text.H4 fontWeight={FontWeight.LIGHT}>
                        <Markdown components={{ p: ({ _node, ...props }) => props.children }}>
                          {template(cfgByCrPerformanceRating.title, {
                            CR: cr.toUpperCase(),
                            CR_PERFORMANCE: metric.performance !== undefined ? (metric.performance * 100).toFixed() : 0,
                          })}
                        </Markdown>
                      </Text.H4>
                    )}

                    {agentsBelowTarget.length > 0 && (
                      <Message
                        data-testid="below-target-agents"
                        message="<u>Alcuni agenti</u> sono sotto la media"
                        icon={ICON_ALERT_CIRCLE_OUTLINE}
                        type="critical"
                        onClick={() => agentsBelowTargetModal.open()}
                      />
                    )}
                  </Stack>
                </Spacing>
              </HStack>

              <Helper
                content={template(cfgByCr.helperContent, {
                  AVG_CR: metric.goal * 100,
                })}
              >
                <Icon
                  path={ICON_INFORMATION_OUTLINE}
                  size={24}
                />
              </Helper>
            </HStack>
          </Spacing>
        </Card.Content>
      </Card>

      <Portal>
        <SimpleModal
          {...agentsBelowTargetModal}
          aria-label="Informazioni agenti sotto la media"
          title={`${cr.toUpperCase()} del team`}
          size={ModalSize.MEDIUM}
        >
          <ManagerConversionRateDetailView
            metric={metric}
            cr={cr}
          />
        </SimpleModal>
      </Portal>
    </>
  );
};

export default ManagerConversionRateSummaryCard;
